import { Search } from "lucide-react";
import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { CamposDeLaFila } from "@/lib/informes";
import { formatearMonto } from "@/lib/montos";
import { cn } from "@/lib/utils";

import { ChipDelCuadro } from "./ChipDelCuadro";
import { CruceConTerreno } from "./CruceConTerreno";
import { EncabezadoNumerado } from "./EncabezadoNumerado";
import { FormularioDeNuevaConstruccion } from "./FormularioDeNuevaConstruccion";
import { TablaDeConstrucciones } from "./TablaDeConstrucciones";
import {
  textoDelResumen,
  vistaDelCuadro,
  type FilaDelCuadro,
  type FiltroDelCuadro,
  type ModoDelTerreno,
  type OrdenDelCuadro,
} from "./cuadroDeConstrucciones";
import { pluralizar } from "./registroDeTerreno";

interface ListaDeConstruccionesProps {
  filas: FilaDelCuadro[];
  modo: ModoDelTerreno;
  encargado: string | null;
  ocupada: boolean;
  onAgregar: (campos: CamposDeLaFila, fotos: File[]) => void;
  onAbrir: (fila: FilaDelCuadro) => void;
  onValidar: (fila: FilaDelCuadro, validada: boolean) => void;
  onValidarVarias: (filaIds: number[]) => void;
  onQuitarVarias: (filaIds: number[]) => void;
}

/**
 * La tarjeta 01: la lista de construcciones del informe.
 *
 * TODO LO QUE FILTRA, ORDENA Y CUENTA vive en `cuadroDeConstrucciones.ts`; acá
 * solo está el estado de la pantalla (qué filtro, qué búsqueda, qué filas
 * están marcadas) y su dibujo.
 *
 * LA SELECCIÓN SE GUARDA POR CLAVE y no por fila: una fila que cambia de
 * posición con el orden o la página sigue siendo la misma marcada.
 */
export function ListaDeConstrucciones({
  filas,
  modo,
  encargado,
  ocupada,
  onAgregar,
  onAbrir,
  onValidar,
  onValidarVarias,
  onQuitarVarias,
}: ListaDeConstruccionesProps) {
  const [formularioAbierto, setFormularioAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [filtro, setFiltro] = useState<FiltroDelCuadro>("todas");
  const [orden, setOrden] = useState<OrdenDelCuadro>("pendientes");
  const [pagina, setPagina] = useState(1);
  const [seleccion, setSeleccion] = useState<ReadonlySet<string>>(new Set());
  const [confirmandoQuitar, setConfirmandoQuitar] = useState(false);

  const vista = useMemo(
    () => vistaDelCuadro(filas, { modo, busqueda, filtro, orden, pagina }),
    [filas, modo, busqueda, filtro, orden, pagina],
  );

  const { resumen } = vista;
  // Sin ninguna construcción el formulario va abierto: es lo único que hay.
  const mostrarFormulario = formularioAbierto || filas.length === 0;

  /* Solo las filas YA guardadas se pueden marcar: validar o quitar es un acto
     sobre una fila del cuadro, y la construcción de terreno sin guardar no lo es. */
  const marcables = vista.visibles.filter((f) => f.id !== null);
  const marcadas = filas.filter((f) => f.id !== null && seleccion.has(f.clave));
  const todasMarcadas = marcables.length > 0 && marcables.every((f) => seleccion.has(f.clave));

  function elegirFiltro(siguiente: FiltroDelCuadro) {
    setFiltro(siguiente);
    setPagina(1);
  }

  function alternar(fila: FilaDelCuadro) {
    setSeleccion((actual) => {
      const siguiente = new Set(actual);

      if (!siguiente.delete(fila.clave)) siguiente.add(fila.clave);

      return siguiente;
    });
    setConfirmandoQuitar(false);
  }

  function alternarTodas() {
    setSeleccion((actual) => {
      const siguiente = new Set(actual);

      marcables.forEach((f) => (todasMarcadas ? siguiente.delete(f.clave) : siguiente.add(f.clave)));

      return siguiente;
    });
    setConfirmandoQuitar(false);
  }

  function limpiarSeleccion() {
    setSeleccion(new Set());
    setConfirmandoQuitar(false);
  }

  const idsMarcados = () => marcadas.map((f) => f.id as number);

  function validarMarcadas() {
    onValidarVarias(idsMarcados());
    limpiarSeleccion();
  }

  function quitarMarcadas() {
    if (!confirmandoQuitar) {
      setConfirmandoQuitar(true);

      return;
    }

    onQuitarVarias(idsMarcados());
    limpiarSeleccion();
  }

  const todasValidadas = resumen.cantidad > 0 && resumen.validadas === resumen.cantidad;
  const filtrosDeEstado: { filtro: FiltroDelCuadro; rotulo: string }[] = [
    ...(modo === "sincronizado"
      ? [{ filtro: "con_notas" as const, rotulo: "Con notas de terreno" }]
      : []),
    { filtro: "sin_valorizar", rotulo: "Sin valorizar" },
    { filtro: "sin_validar", rotulo: "Sin validar" },
  ];

  return (
    <section
      id="construcciones-lista"
      aria-labelledby="construcciones-lista-titulo"
      className="bg-card scroll-mt-5 overflow-hidden rounded-xl border-[1.5px]"
    >
      <div className="px-5 pt-4 pb-3">
        <EncabezadoNumerado
          numero="01"
          idDelTitulo="construcciones-lista-titulo"
          titulo={
            resumen.cantidad > 0
              ? pluralizar(resumen.cantidad, "construcción", "construcciones")
              : "Construcciones"
          }
          bajada={textoDelResumen(resumen)}
          acciones={
            <>
              {resumen.cantidad > 0 ? (
                <>
                  <span
                    className={cn(
                      "rounded-sm px-2 py-1 font-mono text-[0.6875rem] font-bold",
                      todasValidadas
                        ? "bg-success-soft text-success-soft-foreground"
                        : "bg-muted text-ink-body",
                    )}
                  >
                    {resumen.validadas} de {resumen.cantidad} validadas
                  </span>
                  <span className="bg-muted text-ink rounded-sm px-2 py-1 font-mono text-[0.6875rem] font-bold">
                    {formatearMonto(resumen.totalUf, null)} UF
                  </span>
                </>
              ) : null}
              {mostrarFormulario ? null : (
                <Button
                  type="button"
                  size="sm"
                  // El verde más fuerte del diseño (#2b8229), no el primario:
                  // sobre el aviso verde de arriba el primario no se despega.
                  className="bg-primary-fuerte hover:bg-primary-fuerte hover:brightness-90"
                  onClick={() => setFormularioAbierto(true)}
                >
                  + Agregar construcción
                </Button>
              )}
            </>
          }
        />
      </div>

      {mostrarFormulario ? (
        <FormularioDeNuevaConstruccion
          ocupada={ocupada}
          onAgregar={onAgregar}
          onCerrar={() => setFormularioAbierto(false)}
        />
      ) : null}

      {modo === "oculto" || filas.length === 0 ? null : (
        <CruceConTerreno
          modo={modo}
          conteos={vista.conteos}
          filtro={filtro}
          encargado={encargado}
          onElegir={elegirFiltro}
        />
      )}

      {filas.length === 0 ? null : (
        <>
          <div className="border-divisor bg-muted/30 flex flex-wrap items-center gap-2.5 border-t px-5 py-2.5">
            <div className="relative w-[14.375rem] flex-none">
              <Search
                className="text-ink-hint pointer-events-none absolute top-1/2 left-3 size-3.5 -translate-y-1/2"
                aria-hidden="true"
              />
              <Input
                aria-label="Buscar construcción"
                placeholder="Buscar construcción"
                value={busqueda}
                onChange={(e) => {
                  setBusqueda(e.target.value);
                  setPagina(1);
                }}
                className="bg-background h-8 rounded-[0.625rem] pl-8 text-[0.78125rem]"
              />
            </div>

            <div className="flex flex-wrap gap-1.5">
              {filtrosDeEstado.map((f) => (
                <ChipDelCuadro
                  key={f.filtro}
                  activo={filtro === f.filtro}
                  rotulo={f.rotulo}
                  cantidad={vista.conteos[f.filtro]}
                  onClick={() => elegirFiltro(filtro === f.filtro ? "todas" : f.filtro)}
                />
              ))}
            </div>

            <div className="ml-auto flex items-center gap-2">
              <Label htmlFor="construcciones-orden" className="text-ink-muted mb-0 text-[0.71875rem]">
                Ordenar
              </Label>
              <select
                id="construcciones-orden"
                value={orden}
                onChange={(e) => setOrden(e.target.value as OrdenDelCuadro)}
                className="border-input bg-background text-ink h-8 rounded-[0.625rem] border-[1.5px] px-2 text-xs font-bold"
              >
                <option value="pendientes">Pendientes primero</option>
                <option value="nombre">Nombre</option>
                <option value="superficie">Superficie</option>
              </select>
            </div>
          </div>

          {marcadas.length > 0 ? (
            <div className="bg-superficie-fija text-superficie-fija-foreground flex flex-wrap items-center gap-2.5 px-5 py-2">
              <span className="text-[0.78125rem] font-bold">
                {pluralizar(marcadas.length, "seleccionada", "seleccionadas")}
              </span>
              {/* Los tres botones pisan los colores del DS a propósito: el
                  `outline` pinta su propio fondo claro y, sobre esta franja
                  oscura, dejaba texto blanco sobre fondo blanco. */}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={ocupada}
                className="bg-superficie-fija-foreground text-superficie-fija hover:bg-superficie-fija-foreground/90 hover:text-superficie-fija h-auto rounded-[0.5625rem] px-2.5 py-1.5"
                onClick={validarMarcadas}
              >
                Marcar como validadas
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={ocupada}
                title="Se quitan también sus fotos. No se puede deshacer."
                className="border-superficie-fija-foreground/35 text-superficie-fija-foreground hover:bg-superficie-fija-foreground/10 hover:text-superficie-fija-foreground h-auto rounded-[0.5625rem] border-[1.5px] px-2.5 py-1"
                onClick={quitarMarcadas}
              >
                {confirmandoQuitar ? `¿Quitar ${marcadas.length}? Confirmar` : "Quitar"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-superficie-fija-ghost hover:text-superficie-fija-foreground ml-auto h-auto px-1 py-1 hover:bg-transparent"
                onClick={limpiarSeleccion}
              >
                Cancelar selección
              </Button>
            </div>
          ) : null}

          {vista.visibles.length > 0 ? (
            <div className="border-divisor-suave overflow-x-auto border-t">
              <TablaDeConstrucciones
                filas={vista.visibles}
                modo={modo}
                encargado={encargado}
                seleccion={seleccion}
                todasSeleccionadas={todasMarcadas}
                ocupada={ocupada}
                onAlternarSeleccion={alternar}
                onAlternarTodas={alternarTodas}
                onAbrir={onAbrir}
                onValidar={onValidar}
              />
            </div>
          ) : (
            <p className="border-divisor-suave text-ink-muted border-t px-5 py-5.5 text-center text-[0.78125rem] font-semibold">
              Ninguna construcción coincide con la búsqueda o el filtro.
            </p>
          )}

          <div className="border-divisor-suave bg-superficie-sutil flex flex-wrap items-center gap-3.5 border-t px-5 py-2.5">
            {vista.cargarMas > 0 ? (
              <Button type="button" size="sm" variant="outline" onClick={() => setPagina((p) => p + 1)}>
                Cargar {vista.cargarMas} más
              </Button>
            ) : null}
            <span className="text-ink-muted font-mono text-[0.6875rem] font-semibold">
              {vista.mostrando}
            </span>
            <span className="text-ink-muted ml-auto text-[0.6875rem] font-semibold">
              Clic en una fila: detalle, registros de terreno y edición.
            </span>
          </div>
        </>
      )}
    </section>
  );
}
